import { useContext, useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { ListingStateOwnerContext } from './listing-state-context';

const PREFIX = 'hubeex:listings:v1:';

function read<T>(key: string | null, initial: T | (() => T)): T {
  const fallback = typeof initial === 'function' ? (initial as () => T)() : initial;
  if (!key) return fallback;
  try {
    const raw = sessionStorage.getItem(key);
    if (raw === null) return fallback;
    const value: unknown = JSON.parse(raw);
    if (value === null || typeof value !== typeof fallback) return fallback;
    if (Array.isArray(value) !== Array.isArray(fallback)) return fallback;
    if (typeof value === 'number' && (!Number.isFinite(value) || value < 1)) return fallback;
    return value as T;
  } catch {
    return fallback;
  }
}

/** Listing criteria only: never persist checked rows, form drafts or modal state.
 * Session storage survives route changes and reloads, stays per tab and per user.
 * An explicit URL choice can override the remembered value on entry.
 */
export function useListingState<T>(
  scope: string,
  initial: T | (() => T),
  initialOverride?: T,
): [T, Dispatch<SetStateAction<T>>] {
  const owner = useContext(ListingStateOwnerContext);
  const key = owner ? PREFIX + encodeURIComponent(owner) + ':' + scope : null;
  const [snapshot, setSnapshot] = useState(() => ({
    key,
    value: initialOverride ?? read(key, initial),
  }));
  // Reusing a page for another document type/user must not reuse its old filters.
  if (snapshot.key !== key) {
    setSnapshot({ key, value: initialOverride ?? read(key, initial) });
  }
  useEffect(() => {
    if (!key || snapshot.key !== key) return;
    try {
      sessionStorage.setItem(key, JSON.stringify(snapshot.value));
    } catch {
      // Private browsing/storage quota must never prevent filtering.
    }
  }, [key, snapshot]);
  const setValue: Dispatch<SetStateAction<T>> = (next) => {
    setSnapshot((current) => ({
      key,
      value: typeof next === 'function' ? (next as (previous: T) => T)(current.value) : next,
    }));
  };
  return [snapshot.value, setValue];
}
